import { CategoryApi } from "@/api/CategoryApi";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { drain } from "@utils/import/core/drain";
import { buildCabysAuditRows, CabysAuditRow, CabysAuditTarget } from "@utils/cabysAudit";

export type CabysFix = {
    id: string;
    codigo_cabys: string;
    tipo_producto: number;
};

export type CabysFixSummary = {
    updated: string[];
    failed: Array<{ id: string; message: string }>;
};

export type CabysFixProgress = {
    processed: number;
    total: number;
};

/** Pagina todo el inventario del tenant con la seleccion minima de la auditoria. */
const drainProducts = async (): Promise<ProductDataType[]> => {
    const all: ProductDataType[] = [];
    let token: string | undefined = undefined;

    do {
        const { items, nextToken } = await ProductApi.fetchAllForCabysAudit(token);
        all.push(...items);
        token = nextToken;
    } while (token);

    return all;
};

const scan = async (target: CabysAuditTarget): Promise<CabysAuditRow[]> => {
    const items = target === "category" ? await drain(CategoryApi) : await drainProducts();

    return buildCabysAuditRows(items, target);
};

const applyFix = async (target: CabysAuditTarget, fix: CabysFix): Promise<void> => {
    const { id, codigo_cabys, tipo_producto } = fix;

    if (target === "category") {
        await CategoryApi.update(id, { codigo_cabys, tipo_producto });
        return;
    }

    await ProductApi.updateCabysFields(id, { codigo_cabys, tipo_producto });
};

/**
 * Aplica las correcciones una por una.
 *
 * Secuencial y no en paralelo: son updates sobre el mismo indice de DynamoDB y asi se
 * puede reportar progreso real y seguir con las demas cuando una falla.
 */
const applyCabysFixes = async (
    target: CabysAuditTarget,
    fixes: CabysFix[],
    onProgress?: (progress: CabysFixProgress) => void
): Promise<CabysFixSummary> => {
    const summary: CabysFixSummary = { updated: [], failed: [] };
    const total = fixes.length;

    for (const fix of fixes) {
        try {
            await applyFix(target, fix);
            summary.updated.push(fix.id);
        } catch (err) {
            const message = err instanceof Error ? err.message : "No fue posible actualizar el registro";
            summary.failed.push({ id: fix.id, message });
        }
        onProgress?.({ processed: summary.updated.length + summary.failed.length, total });
    }

    if (target === "category") {
        await CategoryApi.invalidateAllByTenant();
        CategoryApi.clearCache();
    }

    return summary;
};

export const CabysAuditApi = {
    scanProductCabys: () => scan("product"),
    scanCategoryCabys: () => scan("category"),
    applyCabysFixes,
};
